import React, { useContext, useEffect, useState } from 'react'
import { STEP2_FORM_NAME } from '../../../univers/ui/subscription/services/souscriptionConstants'
import { FormattedMessage, useIntl } from 'react-intl'
import PageLayout from '../../../components/PageLayout'
import IBAN from 'iban'
import { Form } from 'react-final-form'
import LocalStorage from '../../../business/storage/LocalStorage'
import { ToastType } from '../../../components/toast/toastConstants'
import { ToastContext } from '../../../components/toast/ToastContext'
import GarantieSouscriptionRibSection from '../subscription/components/GarantieSouscriptionRibSection'
import { connect } from 'react-redux'
import { RefreshContext } from '../personalInfo/services/refresh/RefreshContext'
import Loader from '../../../components/Loader'
import ButtonDefault from '../../../components/ButtonDefault'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useBreakpoints } from '../../../components/breakpoints/BreakpointsProvider'
import ContactService from '../../../api/gerep/ContactService'
import ConfirmationPopin from '../../../components/ConfirmationPopin'
import FormatUtils from '../../../utils/FormatUtils'
import { getRibList } from '../../webservices/rib/ribSelector'
import { getAffiliationAssurePrincipal } from '../../webservices/affiliation/affiliationSelector'

type MandatPrelevementProps = {
	assurePrincipal: any
	ribs: any
}

const MandatPrelevementPage: React.FC<MandatPrelevementProps> = ({
	assurePrincipal,
	ribs
}) => {
	const intl = useIntl()
	const { isMobile, isTabletPortrait } = useBreakpoints()
	const { addToast } = useContext(ToastContext)
	const { refreshing } = useContext(RefreshContext)
	const [editRib, setEditRib] = useState<boolean>(false)
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
	const [openConfirmation, setOpenConfirmation] = useState<boolean>(false)
	const [initialValuesFrom, setInitialValuesFrom] = useState<any>()

	const mainRib = ribs.filter((rib: any) => rib.numindiv === assurePrincipal.numIndividu)[0]

	useEffect(() => {
		setInitialValuesFrom({
			[STEP2_FORM_NAME.IBAN]: `${mainRib?.clefIban} ${FormatUtils.formatBban(mainRib?.bban, true)}`,
			[STEP2_FORM_NAME.BANK]: mainRib?.domiciliation,
			[STEP2_FORM_NAME.ACCOUNT_HOLDER]: mainRib?.intitule
		})
	}, [ribs])

	if (refreshing || ribs.length === 0) {
		return <Loader />
	}

	const edit = () => {
		setInitialValuesFrom((prevState: any) => ({
			...prevState,
			[STEP2_FORM_NAME.IBAN]: mainRib?.clefIban + mainRib?.bban
		}))
		setEditRib(true)
	}

	const validateMandatPrelevement = (values: any, errors: any) => {
		let iban
		if (!editRib) {
			iban = mainRib?.clefIban + mainRib?.bban
		} else {
			iban = values[STEP2_FORM_NAME.IBAN]
		}

		if (!iban) {
			errors[STEP2_FORM_NAME.IBAN] = intl.formatMessage({ id: 'global.error.required' })
		} else if (!IBAN.isValid(iban)) {
			errors[STEP2_FORM_NAME.IBAN] = intl.formatMessage({ id: 'mandatPrelevement.errors.iban' })
		}

		if (!values[STEP2_FORM_NAME.BANK]) {
			errors[STEP2_FORM_NAME.BANK] = intl.formatMessage({ id: 'global.error.required' })
		}
		if (!values[STEP2_FORM_NAME.ACCOUNT_HOLDER]) {
			errors[STEP2_FORM_NAME.ACCOUNT_HOLDER] = intl.formatMessage({ id: 'global.error.required' })
		}

		if (!values[STEP2_FORM_NAME.ACCEPT_SEPA] || values[STEP2_FORM_NAME.ACCEPT_SEPA].length === 0) {
			errors[STEP2_FORM_NAME.ACCEPT_SEPA] = intl.formatMessage({ id: 'souscription.form.error.acceptSepa' })
		}
	}

	const onSubmit = (values: any) => {
		setIsButtonLoading(true)

		let data = {
			iban: editRib ? values[STEP2_FORM_NAME.IBAN] : mainRib?.clefIban + mainRib?.bban,
			banque: values[STEP2_FORM_NAME.BANK],
			titulaire: values[STEP2_FORM_NAME.ACCOUNT_HOLDER],
			valideSEPA: values[STEP2_FORM_NAME.ACCEPT_SEPA].length > 0
		}

		ContactService.editMandatPrelevement(data, LocalStorage.getToken())
			.then(() => {
				addToast(ToastType.SUCCESS, 'personalInfo.personalData.modifySS.success')
				setOpenConfirmation(true)
			})
			.catch((e: any) => {
				if (e?.data && Object.keys(e.data).length > 0) {
					const firstKey = Object.keys(e.data)[0]
					const firstMessage = Array.isArray(e.data[firstKey]) ? e.data[firstKey][0] : e.data[firstKey]
					addToast(ToastType.ERROR, firstMessage || 'Une erreur est survenue.')
				} else {
					addToast(ToastType.ERROR, 'Une erreur technique est survenue. Votre demande n\'a pas pu aboutir.')
				}
				return e?.data
			})
			.finally(() => setIsButtonLoading(false))
	}

	return (
		<PageLayout title={intl.formatMessage({ id: 'mandatPrelevement.titre' })}>
			<Form
				initialValues={initialValuesFrom}
				validate={(values) => {
					const errors: any = {}
					validateMandatPrelevement(values, errors)
					return errors
				}}
				onSubmit={onSubmit}
				render={({ handleSubmit, values, errors }) => {
					return (<>
							<GarantieSouscriptionRibSection
								editRib={editRib}
								setEditRib={edit}
								values={values}
								rib={mainRib}
								errors={errors}
								mandatPrelevement
							/>
							<Stack alignItems="center" marginTop="32px" marginBottom="-24px">
								<ButtonDefault
									variant="contained"
									type="submit"
									onClick={handleSubmit}
								>
									{isButtonLoading ? <CircularProgress size={24} color="inherit" /> :
										<FormattedMessage id="mandatPrelevement.valider" />}
								</ButtonDefault>
								<Box display="flex" width="100%" flex={1}
								     alignSelf={isMobile || isTabletPortrait ? 'unset' : 'baseline'}>
									<Typography sx={{
										position: isMobile || isTabletPortrait ? 'unset' : 'relative',
										bottom: '32px',
										left: 0,
										height: '32px',
										display: 'flex',
										alignItems: 'flex-end'
									}} variant="body2" color="#7a7a7a">
										<FormattedMessage id="global.mandatory" />
									</Typography>
								</Box>
							</Stack>
							<ConfirmationPopin
								title={intl.formatMessage({ id: 'mandatPrelevement.titre' })}
								open={openConfirmation}
								setOpen={setOpenConfirmation}
							/>
						</>
					)
				}}
			/>
		</PageLayout>
	)
}

const mapStateToProps = (state: any) => ({
	assurePrincipal: getAffiliationAssurePrincipal(state),
	ribs: getRibList(state)
})

export default connect(mapStateToProps)(MandatPrelevementPage)
